import type { ReactNode } from 'react';
import { Card, CardHeader, CardTitle, Columns, type Column } from '@paige/ui';
import { useFormat, type Formatter } from '@paige/format';
import type { FunnelCompany } from './funnel-types';
import {
  LONGEST_BUCKET,
  activationBuckets,
  daysToActivate,
  medianActivationSeconds,
} from './funnel-math';
import { activatedHelp, spanWord } from './funnel-words';

function bucketWord(day: number): string {
  if (day === 0) return 'The day they signed up';
  if (day === LONGEST_BUCKET) return `${LONGEST_BUCKET} days or more`;
  return `${day} ${day === 1 ? 'day' : 'days'} after sign-up`;
}

function columnsOf(buckets: readonly number[], f: Formatter): Column[] {
  return buckets.map((n, day) => ({
    axis: day === LONGEST_BUCKET ? `${day}+` : String(day),
    value: n,
    tip: {
      title: bucketWord(day),
      value: `${f.int(n)} ${n === 1 ? 'company' : 'companies'}`,
      lines: [],
    },
  }));
}

/** The median is First export's "after sign-up" number on the funnel, one function for both. */
function summary(f: Formatter, exported: number, signedUp: number, median: number | null): string {
  const of = `${f.int(exported)} of the ${f.int(signedUp)} ${signedUp === 1 ? 'company' : 'companies'} that signed up in the period have exported`;
  return median === null ? `${of}.` : `${of}; median ${spanWord(median)} after sign-up.`;
}

/**
 * Days from sign-up to first export, a column a day (critique r2, must 3), every bin labelled at its
 * centre and the count axis in whole companies (round 3, dataviz M4).
 */
export function ActivationHistogram({
  companies,
}: {
  companies: readonly FunnelCompany[];
}): ReactNode {
  const f = useFormat();
  const days = daysToActivate(companies);
  const median = medianActivationSeconds(companies);
  return (
    <Card className="ad-funnel__chart">
      <CardHeader>
        <CardTitle>Days from sign-up to activation</CardTitle>
        <span className="ad-small">
          {activatedHelp} {summary(f, days.length, companies.length, median)}
        </span>
      </CardHeader>
      {days.length === 0 ? (
        <p className="ad-small">No company that signed up in the period has exported yet.</p>
      ) : (
        <Columns
          columns={columnsOf(activationBuckets(days), f)}
          label={`Companies by days from sign-up to first export: ${summary(f, days.length, companies.length, median)}`}
          format={f.int}
          height={160}
          counts
          axisEvery={1}
        />
      )}
    </Card>
  );
}
